import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plane, Train, Landmark, Building2, TrendingUp, MapPin, CheckCircle } from "lucide-react";

const LOGO = "/sneha-logo.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const WhyNaviMumbai = () => {
  const drivers = [
    {
      icon: Plane, title: "Navi Mumbai International Airport",
      stat: "₹16,000+ Cr", statLabel: "Investment",
      desc: "India's largest greenfield airport creating a 25km influence zone of high appreciation. Expected to handle 6 crore passengers annually.",
      impact: ["150-200% appreciation in core zones", "2.5 lakh direct + indirect jobs", "₹16,000 Cr Phase 1 investment"]
    },
    {
      icon: Landmark, title: "Atal Setu Sea Bridge",
      stat: "21.8 km", statLabel: "Sea Bridge",
      desc: "India's longest sea bridge now operational, connecting Mumbai to Navi Mumbai in just 20 minutes. Fundamentally changed the commuter landscape.",
      impact: ["2 hours to 20 minutes travel time", "70,000+ daily commuters", "35% property premium in connected zones"]
    },
    {
      icon: Train, title: "Metro Rail Network",
      stat: "36+", statLabel: "Stations Planned",
      desc: "6 metro corridors transforming intra-city connectivity. Properties within 500m of metro stations command significant rental and resale premiums.",
      impact: ["40% rental premium near stations", "6 metro corridors planned", "Seawoods to Kharghar in 15 mins"]
    },
    {
      icon: Building2, title: "NAINA Smart City",
      stat: "30,000 Ha", statLabel: "Development Zone",
      desc: "Navi Mumbai Airport Influence Notified Area — a mega planned city for 30 lakh residents. The most ambitious urban development project in India.",
      impact: ["₹3 Lakh Crore investment planned", "30 lakh population by 2035", "200%+ appreciation target by 2035"]
    },
  ];

  const comparisons = [
    { area: "South Mumbai", price: "₹50,000+/sqft", growth: "5-8%/yr", infra: "Saturated" },
    { area: "Bandra-Kurla", price: "₹35,000+/sqft", growth: "6-10%/yr", infra: "Mature" },
    { area: "Thane", price: "₹12,000/sqft", growth: "10-12%/yr", infra: "Developing" },
    { area: "Navi Mumbai", price: "₹8,000/sqft", growth: "15-20%/yr", infra: "Explosive", highlight: true },
    { area: "Panvel/Ulwe", price: "₹6,500/sqft", growth: "18-25%/yr", infra: "Nascent", highlight: true },
  ];

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Header */}
      <div className="py-16 border-b border-white/5"
        style={{ background: "linear-gradient(180deg, #0a0900 0%, #080808 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="section-label">
            Market Intelligence
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3]">
            Why Smart Money<br />
            <span className="text-gold-gradient italic">Is Moving Here</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-[#666] text-sm max-w-2xl mt-4 leading-relaxed">
            4 transformational infrastructure projects are creating India's next major real estate appreciation corridor. Here's the complete picture.
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 space-y-20">
        {/* Infrastructure drivers */}
        <div>
          <motion.p {...fadeUp} className="section-label mb-8 text-center">4 Growth Drivers</motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {drivers.map((d, i) => (
              <motion.div key={d.title}
                {...fadeUp} transition={{ delay: i * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="glass-gold p-8 gold-card"
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                    <d.icon size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-3 mb-1">
                      <span className="font-display text-2xl text-[#D4AF37]">{d.stat}</span>
                      <span className="text-xs text-[#666]">{d.statLabel}</span>
                    </div>
                    <h3 className="font-heading text-base text-[#FAF8F3] font-light">{d.title}</h3>
                  </div>
                </div>
                <p className="text-[#A0A0A0] text-sm leading-relaxed mb-5">{d.desc}</p>
                <div className="space-y-2">
                  {d.impact.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle size={11} className="text-[#D4AF37] shrink-0" />
                      <span className="text-xs text-[#666]">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Price comparison */}
        <div>
          <motion.p {...fadeUp} className="section-label mb-3">Price Comparison</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-3xl text-[#FAF8F3] font-light mb-8">
            Why Navi Mumbai Is<br />
            <span className="text-gold-gradient italic">The Smart Entry Point</span>
          </motion.h2>
          <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left py-3 px-4 text-xs tracking-wider text-[#D4AF37] uppercase">Location</th>
                  <th className="text-left py-3 px-4 text-xs tracking-wider text-[#D4AF37] uppercase">Price/sqft</th>
                  <th className="text-left py-3 px-4 text-xs tracking-wider text-[#D4AF37] uppercase">Avg Growth</th>
                  <th className="text-left py-3 px-4 text-xs tracking-wider text-[#D4AF37] uppercase">Infrastructure</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <motion.tr key={row.area}
                    initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                    className={`border-b border-white/5 ${row.highlight ? "bg-[#D4AF37]/5" : ""}`}
                  >
                    <td className="py-4 px-4">
                      <span className={`text-sm font-light ${row.highlight ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>
                        {row.area}
                        {row.highlight && <span className="ml-2 text-[9px] border border-[#D4AF37]/30 px-1.5 py-0.5 tracking-wider">OUR FOCUS</span>}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-[#A0A0A0]">{row.price}</td>
                    <td className="py-4 px-4">
                      <span className={`text-sm font-display ${row.highlight ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>{row.growth}</span>
                    </td>
                    <td className="py-4 px-4 text-xs text-[#666]">{row.infra}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div {...fadeUp} className="text-center glass-gold p-12">
          <img src={LOGO} alt="Sneha Reality" className="h-16 w-auto object-contain mx-auto mb-6" />
          <h3 className="font-display text-3xl text-[#FAF8F3] mb-3">The Entry Window Is Now</h3>
          <p className="text-[#A0A0A0] text-sm mb-8 max-w-md mx-auto">
            Infrastructure investments of this scale take 5-10 years to play out fully. Early movers capture the maximum appreciation. Don't wait.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/properties" className="btn-gold-solid btn-gold text-xs py-3 px-8">
              Explore Properties <ArrowRight size={12} className="inline ml-2" />
            </Link>
            <Link to="/consultation" className="btn-gold text-xs py-3 px-8">
              Book Private Advisory
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhyNaviMumbai;
